// LAS QUE YA NO ESTÁN PERO SIGUEN DEBIENDO.
//
// Por qué existe esta pantalla (Carlos, 17-sep-2026, tras el caso de Esthela Ramírez):
// la mensualidad se genera el día 1 para toda alumna con clase vigente, pero la baja llega
// después. Quien se da de baja el día 11 sin haber pisado el estudio se queda con el mes
// completo colgado — y NO se ve, porque «Por cobrar» y la lista de adeudos filtran por alumna
// activa. El 17-sep había 13 alumnas con $11,100 invisibles. Si alguien reactiva una de esas
// fichas, el adeudo reaparece y se le cobra un mes que no tomó.
//
// 🚨 Esta pantalla NO borra nada sola, a propósito. Un cargo que se perdona solo es un cargo
// que nadie decidió perdonar: con 13 nombres al mes eso son ~$130k al año desapareciendo sin
// que el dueño se entere. Aquí se PROPONE y Karla decide, y queda quién, cuándo y por qué.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserMinus, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { getBajasConCargo, cancelDebt, moneyEntero as money, BajaConCargo, BajasResp } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

/** 'YYYY-MM-DD' → '27 ago' (sin desfase de zona). */
function fmtShort(d: string | null) {
  if (!d) return '—';
  const [y, m, dd] = d.split('-').map(Number);
  if (!y || !m || !dd) return d;
  const o: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (y !== new Date().getFullYear()) o.year = 'numeric';
  return new Date(y, m - 1, dd).toLocaleDateString('es-MX', o);
}
function monthLabel(m: string | null) {
  if (!m) return '';
  const [y, mm] = m.split('-').map(Number);
  return new Date(y, mm - 1, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}
function concepto(d: BajaConCargo) {
  if (d.debt_type === 'late_fee') return 'Pago tardío';
  return d.membership_name || d.comment || 'Mensualidad';
}

export default function BajasConCargo() {
  const { user } = useAuth();
  const toast = useToast();
  // `adaptUser` ya traduce owner/manager → 'admin' (AuthContext), igual que DebtsPanel.
  const puedeGestionar = user?.role === 'admin';
  const [resp, setResp] = useState<BajasResp | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [pidiendo, setPidiendo] = useState<number | 'todas' | null>(null);
  const [guardando, setGuardando] = useState(false);

  const reload = useCallback(() => { getBajasConCargo().then(setResp).catch(() => setResp(null)); }, []);
  useEffect(() => { reload(); }, [reload]);

  if (!resp || !resp.data.length) return null;

  const { summary, data } = resp;
  // Las que no pisaron el estudio ni un día del mes que se les cobra: el grupo sobre el que
  // perdonar es incontestable. Las demás sí fueron a clase, así que se listan aparte y NO
  // entran en el botón de perdonar en bloque.
  const sinPisar = data.filter((d) => !Number(d.attended_in_month));
  const conClases = data.filter((d) => Number(d.attended_in_month) > 0);

  async function perdonar(ids: number[], texto: string) {
    setGuardando(true);
    let ok = 0; let fallo = 0;
    for (const id of ids) {
      try { await cancelDebt(id, texto); ok++; } catch { fallo++; }
    }
    setGuardando(false); setPidiendo(null); setMotivo('');
    if (ok) toast.success(ok === 1 ? 'Cargo perdonado' : `${ok} cargos perdonados`);
    if (fallo) toast.error(`${fallo} no se pudieron perdonar`);
    reload();
  }

  const totalSinPisar = sinPisar.reduce((s, d) => s + Number(d.remaining || 0), 0);

  return (
    <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.04]">
      <button onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left sm:px-5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-500/15 text-amber-600">
          <UserMinus size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-content">
            {summary.students} {summary.students === 1 ? 'alumna que ya no está sigue' : 'alumnas que ya no están siguen'} con {money(summary.total)} en cargos
          </span>
          <span className="block text-xs text-content-2">
            No aparecen en «Por cobrar» porque están dadas de baja — pero el cargo existe y vuelve si reactivas su ficha
          </span>
        </span>
        {abierto ? <ChevronDown size={18} className="text-content-2" /> : <ChevronRight size={18} className="text-content-2" />}
      </button>

      {abierto && (
        <div className="border-t border-amber-500/20 px-4 pb-4 pt-3 sm:px-5">
          {sinPisar.length > 0 && (
            <>
              <p className="mb-2 text-xs text-content-2">
                <b className="text-content">{sinPisar.length}</b> no pisaron el estudio ni un día del mes que se les cobra.
              </p>
              <ul className="divide-y divide-line/60 rounded-xl border border-line bg-surface">
                {sinPisar.map((d) => (
                  <li key={d.debt_id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
                    <PersonAvatar contactId={d.contact_id} hasPhoto={d.has_photo} name={d.student_name} size="h-8 w-8" />
                    <span className="min-w-0 flex-1">
                      <Link to={`/alumnos/${d.contact_id}`} className="block truncate text-sm font-medium text-content hover:underline">
                        {d.student_name}
                      </Link>
                      <span className="block text-xs text-content-2">
                        {concepto(d)} · {monthLabel(d.month)} · última clase {fmtShort(d.last_attendance)} · último pago {fmtShort(d.last_payment)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-content">{money(d.remaining)}</span>
                    {puedeGestionar && (
                      <button onClick={() => { setPidiendo(d.debt_id); setMotivo('Se dio de baja sin tomar ninguna clase del mes'); }}
                        className="rounded-lg border border-line px-2.5 py-1 text-xs text-content-2 hover:text-content">
                        Perdonar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {conClases.length > 0 && (
            <div className="mt-3 rounded-xl border border-line bg-surface p-3">
              <p className="mb-2 flex items-start gap-2 text-xs text-content-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                <span>
                  Estas <b className="text-content">sí tomaron clases</b> del mes que se les cobra. Se quedan fuera del botón
                  de perdonar en bloque: revísalas una por una.
                </span>
              </p>
              <ul className="divide-y divide-line/60">
                {conClases.map((d) => (
                  <li key={d.debt_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                    <PersonAvatar contactId={d.contact_id} hasPhoto={d.has_photo} name={d.student_name} size="h-8 w-8" />
                    <span className="min-w-0 flex-1">
                      <Link to={`/alumnos/${d.contact_id}`} className="block truncate text-sm font-medium text-content hover:underline">
                        {d.student_name}
                      </Link>
                      <span className="block text-xs text-content-2">
                        {concepto(d)} · {monthLabel(d.month)} · <b className="text-content">{d.attended_in_month} {Number(d.attended_in_month) === 1 ? 'clase' : 'clases'}</b> ese mes
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-content">{money(d.remaining)}</span>
                    {puedeGestionar && (
                      <button onClick={() => { setPidiendo(d.debt_id); setMotivo(''); }}
                        className="rounded-lg border border-line px-2.5 py-1 text-xs text-content-2 hover:text-content">
                        Perdonar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {puedeGestionar && sinPisar.length > 1 && pidiendo === null && (
            <button onClick={() => { setPidiendo('todas'); setMotivo('Se dio de baja sin tomar ninguna clase del mes'); }}
              className="mt-3 rounded-lg bg-amber-600 px-3.5 py-2 text-sm font-medium text-white">
              Perdonar las {sinPisar.length} que no pisaron el estudio · {money(totalSinPisar)}
            </button>
          )}

          {pidiendo !== null && (
            <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-3">
              <p className="mb-2 text-xs text-content-2">
                {pidiendo === 'todas'
                  ? `Vas a perdonar ${sinPisar.length} cargos por ${money(totalSinPisar)}. Queda guardado quién, cuándo y por qué; se puede revisar después.`
                  : 'Motivo (queda guardado en el historial del adeudo):'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && motivo.trim()) perdonar(pidiendo === 'todas' ? sinPisar.map((d) => d.debt_id) : [pidiendo], motivo.trim());
                    if (e.key === 'Escape') { setPidiendo(null); setMotivo(''); }
                  }}
                  placeholder="p. ej. se dio de baja el 11 de septiembre"
                  className="min-w-[16rem] flex-1 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content" />
                <button disabled={!motivo.trim() || guardando}
                  onClick={() => perdonar(pidiendo === 'todas' ? sinPisar.map((d) => d.debt_id) : [pidiendo], motivo.trim())}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
                  {guardando ? 'Perdonando…' : 'Perdonar'}
                </button>
                <button onClick={() => { setPidiendo(null); setMotivo(''); }}
                  className="rounded-lg border border-line px-3 py-1.5 text-sm text-content-2">
                  Dejarlo
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
